import { parsePhoneNumber } from 'awesome-phonenumber';
import { defineMessages, IntlShape } from 'react-intl';
import * as Yup from 'yup';

import { COUNTRY_CALLING_CODES } from '../constants';

const PHONE_GROUPS_BY_CALLING_CODE: Record<string | 'default', number[]> = {
  '60': [2, 3, 4], // Malaysia
  '62': [3, 4, 4], // Indonesia
  '63': [3, 3, 4], // Philippines
  '65': [4, 4], // Singapore
  '66': [2, 3, 4], // Thailand
  '84': [3, 3, 3], // Vietnam
  '852': [4, 4], // Hong Kong
  '886': [3, 3, 3], // Taiwan
  default: [4, 4, 4], // Others
};

const PHONE_EXAMPLES: Record<string | 'default', string> = {
  '60': '187654321', // Malaysia
  '62': '812345678', // Indonesia
  '63': '9171234567', // Philippines
  '65': '91234567', // Singapore
  '66': '812345678', // Thailand
  '84': '912345678', // Vietnam
  '852': '91234567', // Hong Kong
  '886': '912345678', // Taiwan
  default: '91223452678', // Others
};

const indonesiaPhoneRegex = /^\+628[0-9]{7,12}$/;

const messages = defineMessages({
  required: {
    id: 'text-this-field-is-required',
    defaultMessage: 'This field is required',
  },
  invalidPhone: {
    id: 'text-enter-valid-phone-number',
    defaultMessage: 'Please enter a valid phone number',
  },
});

export function isValidPhoneNumber(
  callingCode: string,
  phoneNumber: string,
  countryCode?: string
): boolean {
  const pn = `${callingCode}${phoneNumber}`;
  const parsedNumber = parsePhoneNumber(pn, { regionCode: countryCode });

  if (!parsedNumber.valid) {
    return false;
  }

  if (callingCode === COUNTRY_CALLING_CODES.id) {
    return indonesiaPhoneRegex.test(pn);
  }

  return true;
}

export function getCensoredPhonePlaceholder(
  applicantCountryCode?: string
): string {
  // Using applicant country code to get the calling code since we don't have the phone number
  if (applicantCountryCode) {
    const callingCode =
      COUNTRY_CALLING_CODES[applicantCountryCode.toLowerCase()];
    if (callingCode) {
      return `${callingCode}****`;
    }
  }

  return '+****';
}

export const PhoneUtils = {
  callingCodeValidation: (formatMessage: IntlShape['formatMessage']) =>
    Yup.string().required(formatMessage(messages.required)),
  phoneValidation: (formatMessage: IntlShape['formatMessage']) =>
    Yup.string()
      .transform(value => value?.replace(/\s/g, '') ?? '')
      .required(formatMessage(messages.required))
      .test({
        name: 'phone-number',
        message: formatMessage(messages.invalidPhone),
        test: function (phoneNumber = '') {
          return isValidPhoneNumber(
            this.parent.callingCode,
            phoneNumber,
            this.parent.countryCode
          );
        },
      }),
  // Removes the '+' symbol
  normalizeCallingCode: (value?: string): string | undefined =>
    value?.trim().replace(/\D/g, ''),
  formatPhoneNumber: (phone: string, callingCode?: string): string => {
    const normalizedCallingCode = PhoneUtils.normalizeCallingCode(callingCode);
    let groups = normalizedCallingCode
      ? PHONE_GROUPS_BY_CALLING_CODE[normalizedCallingCode]
      : PHONE_GROUPS_BY_CALLING_CODE.default;

    groups = groups ?? PHONE_GROUPS_BY_CALLING_CODE.default;

    const digits = phone.replace(/\s/g, '');
    const parts: string[] = [];
    let currentIndex = 0;

    for (const groupSize of groups) {
      const chunk = digits.slice(currentIndex, currentIndex + groupSize);

      if (!chunk) {
        break;
      }

      parts.push(chunk);
      currentIndex += groupSize;
    }

    const remaining = digits.slice(currentIndex);
    if (remaining) {
      parts.push(remaining);
    }

    return parts.join(' ');
  },
  phoneExample: (callingCode?: string): string => {
    const normalizedCode = PhoneUtils.normalizeCallingCode(callingCode);
    const phone =
      (normalizedCode
        ? PHONE_EXAMPLES[normalizedCode]
        : PHONE_EXAMPLES.default) ?? PHONE_EXAMPLES.default;
    return PhoneUtils.formatPhoneNumber(phone, callingCode);
  },
  phoneMetadata: (
    phone: string | null | undefined
  ): {
    phone: string;
    callingCode?: string;
  } => {
    if (!phone) {
      return { phone: '' };
    }
    for (const callingCode of Object.values(COUNTRY_CALLING_CODES)) {
      if (phone.startsWith(callingCode)) {
        return {
          phone: phone.slice(callingCode.length).trim(),
          callingCode: callingCode,
        };
      }
    }

    return { phone };
  },
};
